// @flow

import React, { useState } from 'react';
import FocusBody from 'components/FocusBody';
import Page from 'components/Page';
import logoBlackWide from 'themes/images/logo-black-wide.png';
import FormRow from 'components/FormRow';
import * as Yup from 'yup';
import { injectIntl } from 'react-intl';
import View from 'components/View';
import { DetailBottomBarConfirm } from 'components/DetailBottomBarItem';
import DetailBottomBar, { NotificationBar } from 'components/DetailBottomBar';
import Heading from 'components/Heading';
import type { Style, Theme } from 'types';
import Text from 'components/Text';
import Version from 'components/Version';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { toast } from 'react-toastify';
import ValidationMessage from '../Registration/components/ValidationMessage';
import ImageBottomBar from '../Registration/components/ImageBottomBar';
import FocusBottomBar from '../Registration/components/FocusBottomBar';
import FocusContent from '../Registration/components/FocusContent';
import TextInput from '../Registration/components/TextInput';

const requestRecoveryCodeMutation = gql`
  mutation recoveryCode($email: String) {
    recoveryCode(data: { email: $email }) {
      user {
        record
        username
        name
        email
        phoneNumber
      }
    }
  }
`;

const styles = {
  page: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  text: (props, { components: { FocusPage } }) => ({
    ...FocusPage.text
  }),
  img: {
    marginLeft: 20,
    height: 20
  },
  error: (props, { components: { Login } }) => ({
    ...Login.error
  }),
  header: (props, theme: Theme): Style => ({
    ...theme.components.ExternalModule.SpecificCodeHeader,
    fontSize: '18px',
    width: '420px',
    alignItems: 'flex-start'
  }),
  regCodeText: {
    padding: '15px',
    width: '420px',
    backgroundColor: '#F1F1F1'
  },
  withValidationMessage: {
    left: 'calc(100% + 15px)',
    top: '32px'
  }
};

const SendResetEmailForm = ({ intl, returnHome }) => {
  const [email, setEmail] = useState('');
  const [emailInputError, setEmailInputError] = useState(false);

  const validationSchema = () =>
    Yup.object().shape({
      email: Yup.string()
        .email(intl.formatMessage({ id: 'errors.wrong-email-format' }))
        .required(intl.formatMessage({ id: 'general.required-field' }))
    });

  const validateForm = afterSuccess => {
    validationSchema()
      .isValid({
        email
      })
      .then(valid => {
        if (valid) {
          setEmailInputError(false);
          afterSuccess();
        } else {
          setEmailInputError(true);
        }
      });
  };

  const handleChange = fieldName => e => {
    const fieldValue = e.target.value;

    validationSchema()
      .isValid({
        [fieldName]: fieldValue
      })
      .then(valid => {
        setEmail(fieldValue);
        setEmailInputError(!valid);
      });
  };

  const ValidationMessageRef = emailInputError ? (
    <ValidationMessage
      message={intl.formatMessage({ id: 'reset-password.email.validation' })}
      tipPosition="left"
      style={styles.withValidationMessage}
    />
  ) : (
    <span />
  );

  const DynamicBottomBar = ({ recoveryCode, loading, error }) => (
    <View style={{ flexDirection: 'column' }}>
      {error && <NotificationBar text={intl.formatMessage({ id: 'errors.general-error' })} />}

      <FocusBottomBar>
        <ImageBottomBar>
          <img src={logoBlackWide} alt="LoyalMAN" style={styles.img} />
          <Version position="absolute" />
        </ImageBottomBar>

        <DetailBottomBar withShadow="false">
          <DetailBottomBarConfirm
            name={
              loading
                ? intl.formatMessage({ id: 'general.sending' })
                : intl.formatMessage({ id: 'general.send' })
            }
            onClick={() => {
              validateForm(() => {
                recoveryCode().then(() => {
                  toast.success(
                    intl.formatMessage({ id: 'reset-password.email.success' }, { email }),
                    {
                      autoClose: 30000,
                      position: 'top-center'
                    }
                  );
                  setEmail('');
                  returnHome();
                });
              });
            }}
          />
        </DetailBottomBar>
      </FocusBottomBar>
    </View>
  );

  return (
    <Page title={intl.formatMessage({ id: 'reset-password.email.page-title' })}>
      <View isExpanded style={styles.page}>
        <View style={{ width: 500 }}>
          <FocusContent>
            <FocusBody>
              <View
                isExpanded
                isVertical
                style={{ padding: 40, paddingBottom: 10, alignItems: 'center', width: '100%' }}>
                <FormRow>
                  <Heading style={styles.header} scale={1.5}>
                    {intl.formatMessage({ id: 'reset-password.email.heading' })}
                  </Heading>
                </FormRow>
                <FormRow>
                  <Text style={styles.regCodeText}>
                    {intl.formatMessage({ id: 'reset-password.email.text' })}
                  </Text>
                </FormRow>
                <FormRow>
                  <TextInput
                    hasError={emailInputError}
                    label={intl.formatMessage({ id: 'reset-password.email.label' })}
                    value={email}
                    styleInput={{ width: '420px' }}
                    styleBox={{ flexDirection: 'column', alignItems: 'flex-start' }}
                    onChange={handleChange('email')}
                  />
                  {ValidationMessageRef}
                </FormRow>
              </View>

              <Mutation
                mutation={requestRecoveryCodeMutation}
                variables={{ email }}
                onCompleted={() => {}}>
                {(recoveryCode, { error, loading }) => (
                  <DynamicBottomBar recoveryCode={recoveryCode} loading={loading} error={error} />
                )}
              </Mutation>
            </FocusBody>
          </FocusContent>
        </View>
      </View>
    </Page>
  );
};

export default injectIntl(SendResetEmailForm);
